import {
  PrilohaVestnika as PrilohaVestnikaGenerated,
  PrispevokVestnika as PrispevokVestnikaGenerated,
  PrispevokVestnikaDetail,
  PrispevokVestnikaDetailApi,
} from 'vestniky-frontend-ts-axios';
import { mapIriToFullName, reformatIri } from 'slovlex-idsk-react-components';
import apiConfiguration from './configuration/apiConfiguration';
import { vestnikyPrefixPath } from './configuration/apiConfigurationConstants';
import { ClientResponse } from '../model/types/ClientResponse';
import PrispevokVestnika from '../model/PrispevokVestnika';
import PrispevokVestnikaInfo from '../model/PrispevokVestnikaInfo';
import PrilohaVestnika from '../model/PrilohaVestnika';
import PredpisZbierky from '../model/PredpisZbierky';
import { getSearchData } from './external/indexer/vyhladavanieVyberPredpisuClient';

const client = new PrispevokVestnikaDetailApi(apiConfiguration(vestnikyPrefixPath));

function mapToPrispevky(prispevkyVestnika: Array<PrispevokVestnikaGenerated>): Array<PrispevokVestnikaInfo> {
  return prispevkyVestnika?.map(prispevok => ({
    uuid: prispevok.uuid,
    vestnikUuid: prispevok.vestnik?.uuid,
    cislo: prispevok.cislo,
    nazov: prispevok.nazov,
    kratkyNazov: prispevok.kratkyNazov,
    // eslint-disable-next-line max-len
    menovka: `${prispevok.vestnik?.skratenyNazov} ${prispevok.cislo}/${prispevok.datumZverejnenia?.split('-')[0]} ${prispevok.nazov}`,
    // eslint-disable-next-line max-len
    menovkaSkratena: `${prispevok.vestnik?.skratenyNazov} ${prispevok.cislo}/${prispevok.datumZverejnenia?.split('-')[0]}`,
    vestnik: { ...prispevok.vestnik },
  }));
}

// eslint-disable-next-line max-len
function mapToPrilohy(prilohy: Array<PrilohaVestnikaGenerated>): Array<PrilohaVestnika> {
  return prilohy?.map(priloha => ({
    uuid: priloha.uuid,
    nazovSuboru: priloha.nazov,
    hlavna: priloha.hlavna,
    originalFilename: priloha.originalFilename,
    fileSize: priloha.fileSize,
    file: new File([''], priloha.originalFilename),
    fileBase64: '',
  }));
}

const map = (prispevok: PrispevokVestnikaDetail): PrispevokVestnika => ({
  uuid: prispevok.uuid,
  vestnik: { ...prispevok.vestnik },
  cislo: prispevok.cislo,
  nazov: prispevok.nazov,
  kratkyNazov: prispevok.kratkyNazov,
  datumZverejnenia: prispevok.datumZverejnenia ? new Date(prispevok.datumZverejnenia) : null,
  datumUcinnosti: prispevok.datumUcinnosti ? new Date(prispevok.datumUcinnosti) : null,
  popis: prispevok.popis,
  meniPrispevky: mapToPrispevky(prispevok.meniPrispevky),
  rusiPrispevky: mapToPrispevky(prispevok.rusiPrispevky),
  vykonavaciePredpisy: [],
  prilohy: mapToPrilohy(prispevok.prilohy),
});

const mapPredpisZbierkyList = (
  nazvy: object,
  vykonavaciePredpisy: Array<string>,
): Array<PredpisZbierky> => vykonavaciePredpisy.map(vykonavaciPredpis => {
  const { cesta, iri } = reformatIri(vykonavaciPredpis);
  const fullName = mapIriToFullName(cesta, iri);
  const nazov = nazvy ? nazvy[vykonavaciPredpis].substring(nazvy[vykonavaciPredpis].indexOf('-') + 2) : undefined;
  const popis = !nazov
    ? fullName
    : `${fullName} - ${nazov}`;
  return {
    iri: vykonavaciPredpis,
    cislo: fullName,
    popis,
    redirectUrl: `/ezbierky/pravne-predpisy${vykonavaciPredpis}`,
  };
});

const fetchNazvy = async (vykonavaciePredpisy, options)
  : Promise<object> => {
  const popisList = await Promise.all(vykonavaciePredpisy.map(async vykonavaciPredpis => {
    const hashIndex = vykonavaciPredpis.indexOf('#');
    const uri = hashIndex < 0 ? vykonavaciPredpis : vykonavaciPredpis.substring(0, hashIndex);
    const split = uri.split('/');
    const dopyt = `"${split[4]}/${split[3]}"`;
    const resp = await getSearchData(
      dopyt,
      '/vyhladavanie/predpisZbierky/navrhy',
      '/ezbierky/pravne-predpisy',
      options,
    );
    return [vykonavaciPredpis, resp && resp.data && resp.data.length > 0 ? resp.data[0].nazov : undefined];
  }));
  return Object.fromEntries(popisList);
};

export const fetchPrispevokVestnikaDetail = async (
  uuid: string,
  options?: { headers?: object },
):
  Promise<ClientResponse<PrispevokVestnika>> => {
  const {
    data: prispevokVestnikaDetail,
    status: prispevokVestnikaDetailStatus,
  } = await client.getPrispevokVestnikaDetail(uuid, options);

  const resultData = map(prispevokVestnikaDetail);
  if (prispevokVestnikaDetail?.vykonavaciePredpisy?.length > 0) {
    const nazvy = await fetchNazvy(prispevokVestnikaDetail?.vykonavaciePredpisy, options);
    resultData.vykonavaciePredpisy = mapPredpisZbierkyList(
      nazvy,
      prispevokVestnikaDetail.vykonavaciePredpisy,
    );
  }
  return {
    data: resultData,
    responseCode: prispevokVestnikaDetailStatus,
  };
};
